"use client";

import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PhoneInput } from "./phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import clsx from "clsx";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { toast } from "sonner";
import Spinner from "@/components/ui/spinner";

const formSchema = z.object({
  firstName: z
    .string()
    .min(1, { message: "First Name is required" })
    .min(3, { message: "must be at least 3 characters" }),
  lastName: z
    .string()
    .min(1, { message: "Last Name is required" })
    .min(3, { message: "must be at least 3 characters" }),
  email: z
    .string()
    .min(1, { message: "Email address is required" })
    .email({ message: "Invalid email address" }),
  phoneNumber: z
    .string()
    .min(1, { message: "Phone number is required" })
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  message: z.string().min(1, { message: "Message is required" }),
});

export const contactFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  message: "",
};

function ContactForm() {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: contactFormData,
  });
  const { errors } = form.formState;
  const isTablet = useMediaQuery("(min-width: 768px)");
  const [messageBoxRow, setMessageBoxRow] = useState(6);

  const onSubmit = async (data: typeof contactFormData) => {
    try {
      const res = await fetch("/api/send", {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error("Failed to send");

      toast.success("Message sent successfully!");
      form.reset();
    } catch (error) {
      console.log("Error sending message:", error);
      toast.error("Something went wrong. Please try again later.");
    }
  };

  useEffect(() => {
    setMessageBoxRow(isTablet ? 6 : 4);
  }, [isTablet]);

  return (
    <div className="relative flex w-full max-w-xl flex-col gap-2 bg-white p-5 shadow-lg drop-shadow-md md:p-10">
      {form.formState.isSubmitting && (
        <div className="absolute left-0 top-0 z-50 flex h-full w-full flex-col items-center justify-center bg-slate-800 bg-opacity-70">
          <Spinner />
          <p className="text-center text-lg font-semibold text-white">
            Your message is en route — hard hats on...
          </p>
        </div>
      )}
      <Form {...form}>
        <form
          className="my-5 flex w-full flex-col gap-3 text-base"
          onSubmit={form.handleSubmit(onSubmit)}
          data-testid="contact-form"
        >
          <div className="flex w-full flex-col gap-4 md:flex-row lg:flex-col xl:flex-row">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className={clsx(
                        errors.firstName && "ring-2 ring-red-500",
                        "rounded-none border bg-gray-50 p-2",
                      )}
                      data-testid="first-name-input"
                    />
                  </FormControl>
                  <FormMessage data-testid="first-name-error" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className={clsx(
                        errors.lastName && "ring-2 ring-red-500",
                        "rounded-none border bg-gray-50 p-2",
                      )}
                      data-testid="last-name-input"
                    />
                  </FormControl>
                  <FormMessage data-testid="last-name-error" />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <PhoneInput
                    {...field}
                    className={errors.phoneNumber && "ring-2 ring-red-500"}
                    data-testid="phone-input"
                  />
                </FormControl>
                <FormMessage data-testid="phone-error" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className={clsx(
                      errors.email && "ring-2 ring-red-500",
                      "rounded-none border bg-gray-50 p-2",
                    )}
                    data-testid="email-input"
                  />
                </FormControl>
                <FormMessage data-testid="email-error" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={messageBoxRow}
                    className={clsx(
                      errors.message && "ring-2 ring-red-500",
                      "resize-none rounded-none border bg-gray-50 p-2",
                    )}
                    data-testid="message-input"
                  />
                </FormControl>
                <FormMessage data-testid="message-error" />
              </FormItem>
            )}
          />
          <div className="mt-5 flex w-full justify-end">
            <Button
              className="w-full rounded-sm px-10 shadow-sm drop-shadow-sm md:w-auto"
              type="submit"
              disabled={form.formState.isSubmitting}
              data-testid="submit-button"
            >
              Send Message
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default ContactForm;
